import { ConceptTotalsDto } from '../../../../models/Entities/Contracts/Statistics/ContractStatisticsModels';
import { BillingMatrix } from './BillingControl.Functions';

/**
 * Lo pactado contra lo cobrado.
 *
 * El contrato dice cuánto vale cada concepto y en cuántas visitas se cobra; los
 * consumos dicen cuánto se cobró de verdad. Ninguna de las dos vistas sola contesta
 * la pregunta del administrativo —"¿estoy cobrando lo que pacté?"—; el cruce sí.
 *
 * El pactado sale de la API de estadísticas del contrato y lo cobrado de la matriz
 * que ya se calcula con los consumos.
 */

export type AgreedStatus = 'none' | 'partial' | 'complete' | 'over';

export interface AgreedRow {
    key: string;
    conceptId?: number;
    name: string;
    agreedAmount: number;
    agreedVisits: number;
    billedAmount: number;
    billedCount: number;
    /** Cobrado menos pactado: negativo es plata que falta cobrar. */
    difference: number;
    /** Cobrado sobre pactado, en porcentaje. null si no hay pactado contra qué comparar. */
    coverage: number | null;
    status: AgreedStatus;
    /** Se cobró sin estar en el pactado que devolvió la API. */
    isUnexpected: boolean;
}

/** Margen de tolerancia: por redondeos, 100% exacto casi nunca se da. */
const COMPLETE_FROM = 95;
const COMPLETE_TO = 105;

const statusOf = (agreedAmount: number, billedAmount: number, coverage: number | null): AgreedStatus => {
    if (agreedAmount <= 0) return billedAmount > 0 ? 'over' : 'none';
    if (billedAmount <= 0) return 'none';
    if (coverage === null) return 'none';
    if (coverage < COMPLETE_FROM) return 'partial';
    if (coverage > COMPLETE_TO) return 'over';
    return 'complete';
};

/**
 * Marcas de acento combinables. Se arman por código a propósito: escritas
 * literalmente quedan invisibles en el archivo y se pierden en cualquier copiado.
 */
const DIACRITICS = new RegExp('[' + String.fromCharCode(0x300) + '-' + String.fromCharCode(0x36f) + ']', 'g');

/** Minúsculas y sin tildes, para cruzar por nombre cuando los ids no coinciden. */
const normalize = (value?: string | null): string =>
    (value ?? '')
        .normalize('NFD')
        .replace(DIACRITICS, '')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim();

export interface AgreedTotals {
    agreed: number;
    billed: number;
    difference: number;
    coverage: number | null;
    /** Conceptos pactados sin un solo cobro. */
    neverBilled: number;
    /** Conceptos cobrados que no figuran en lo pactado. */
    unexpected: number;
}

/**
 * Cruza concepto por concepto.
 *
 * El cruce es por id, y si no hay coincidencia se intenta por nombre: la API de
 * estadísticas y la lista de conceptos del contrato no siempre numeran igual, y es
 * preferible cruzar por nombre a mostrar dos filas de lo mismo. Lo que aun así no
 * cruza se marca como cobrado fuera de lo pactado, en vez de esconderlo.
 */
export const buildAgreedVsBilled = (agreed: ConceptTotalsDto[], matrix: BillingMatrix): AgreedRow[] => {
    const billedById = new Map<number, { amount: number; count: number; label: string }>();
    const billedByName = new Map<string, { amount: number; count: number; label: string }>();

    matrix.rows.forEach((row) => {
        const entry = { amount: row.total.amount, count: row.total.count, label: row.label };
        if (row.conceptId) billedById.set(row.conceptId, entry);
        billedByName.set(normalize(row.label), entry);
    });

    const used = new Set<string>();

    const rows: AgreedRow[] = (agreed ?? []).map((item) => {
        const byId = item.conceptId ? billedById.get(item.conceptId) : undefined;
        const byName = billedByName.get(normalize(item.conceptName));
        const billed = byId ?? byName;

        if (billed) used.add(normalize(billed.label));

        const agreedAmount = item.totalAmount || 0;
        const billedAmount = billed?.amount ?? 0;
        const coverage = agreedAmount > 0 ? Math.round((billedAmount / agreedAmount) * 1000) / 10 : null;

        return {
            key: `agreed-${item.conceptId}-${item.contractAddendumId}`,
            conceptId: item.conceptId,
            name: item.conceptName || `Concepto #${item.conceptId}`,
            agreedAmount,
            agreedVisits: item.totalVisits || 0,
            billedAmount,
            billedCount: billed?.count ?? 0,
            difference: billedAmount - agreedAmount,
            coverage,
            status: statusOf(agreedAmount, billedAmount, coverage),
            isUnexpected: false
        };
    });

    // Lo que se cobró y no aparece en lo pactado: se muestra igual, marcado.
    matrix.rows
        .filter((row) => row.total.amount !== 0 && !used.has(normalize(row.label)))
        .forEach((row) => {
            rows.push({
                key: `billed-${row.key}`,
                conceptId: row.conceptId,
                name: row.label,
                agreedAmount: 0,
                agreedVisits: 0,
                billedAmount: row.total.amount,
                billedCount: row.total.count,
                difference: row.total.amount,
                coverage: null,
                status: 'over',
                isUnexpected: true
            });
        });

    // Primero lo que más se aleja de lo pactado: es lo que hay que mirar.
    return rows.sort((a, b) => a.difference - b.difference || b.agreedAmount - a.agreedAmount);
};

export const agreedTotals = (rows: AgreedRow[]): AgreedTotals => {
    const agreed = rows.reduce((acc, row) => acc + row.agreedAmount, 0);
    const billed = rows.reduce((acc, row) => acc + row.billedAmount, 0);

    return {
        agreed,
        billed,
        difference: billed - agreed,
        coverage: agreed > 0 ? Math.round((billed / agreed) * 1000) / 10 : null,
        neverBilled: rows.filter((row) => row.agreedAmount > 0 && row.billedAmount === 0).length,
        unexpected: rows.filter((row) => row.isUnexpected).length
    };
};

export interface AgreedStageRow {
    stageId: number;
    name: string;
    agreedAmount: number;
    agreedVisits: number;
    billedAmount: number;
    billedCount: number;
    difference: number;
    coverage: number | null;
}

/** El mismo cruce, pero por visita del contrato. */
export const buildAgreedByStage = (agreed: ConceptTotalsDto[], matrix: BillingMatrix): AgreedStageRow[] => {
    const stages = new Map<number, AgreedStageRow>();

    (agreed ?? []).forEach((concept) => {
        (concept.stageBreakdowns ?? []).forEach((stage) => {
            const current = stages.get(stage.stageId) ?? {
                stageId: stage.stageId,
                name: stage.stageName || `Visita #${stage.stageId}`,
                agreedAmount: 0,
                agreedVisits: 0,
                billedAmount: 0,
                billedCount: 0,
                difference: 0,
                coverage: null
            };
            current.agreedAmount += stage.amount || 0;
            current.agreedVisits += stage.visits || 0;
            stages.set(stage.stageId, current);
        });
    });

    stages.forEach((row) => {
        const column = matrix.columns.find((item) => item.key === String(row.stageId));
        row.billedAmount = column?.total.amount ?? 0;
        row.billedCount = column?.total.count ?? 0;
        row.difference = row.billedAmount - row.agreedAmount;
        row.coverage = row.agreedAmount > 0 ? Math.round((row.billedAmount / row.agreedAmount) * 1000) / 10 : null;
    });

    return Array.from(stages.values()).sort((a, b) => a.difference - b.difference);
};
